import React, { useState } from 'react';
import packageJson from '../../package.json';

const APP_URL = 'https://app.lawnsmartapp.com';
const WWW_ORIGIN = 'https://www.lawnsmartapp.com';
const DISMISS_KEY = 'lawnsmart-migration-dismissed';
const CUTOVER_DATE = 'May 18, 2026';

const T = {
  green800:  '#1a4a1c',
  green700:  '#1e5e20',
  green600:  '#2a7a2d',
  green200:  '#b8e0bb',
  green50:   '#f0f9f1',
  teal:      '#1ec98a',
  amber:     '#c8761a',
  amberLight:'#fef3e2',
  bg:        '#f5f8f5',
  white:     '#ffffff',
  text:      '#1a2e1b',
  textMed:   '#4a6a4c',
  textLight: '#8aaa8c',
  border:    '#e0ece0',
  shadow:    'rgba(30,80,32,0.12)',
};

const font = "'DM Sans', -apple-system, sans-serif";

function hasMeaningfulData() {
  const tasks    = localStorage.getItem('lawnCareCompletedTasks');
  const settings = localStorage.getItem('lawnCareSettings');
  const notes    = localStorage.getItem('lawn-smart-monthly-notes');
  return (tasks && tasks !== '[]') || !!settings || !!notes;
}

function exportData() {
  const data = {
    completedTasks: JSON.parse(localStorage.getItem('lawnCareCompletedTasks') || '[]'),
    taskNotes:      JSON.parse(localStorage.getItem('lawnCareTaskNotes') || '{}'),
    equipment:      JSON.parse(localStorage.getItem('lawnCareEquipment') || '[]'),
    photos:         JSON.parse(localStorage.getItem('lawnCarePhotos') || '[]'),
    settings:       JSON.parse(localStorage.getItem('lawnCareSettings') || '{}'),
    monthlyNotes:   JSON.parse(localStorage.getItem('lawn-smart-monthly-notes') || '{}'),
    exportDate: new Date().toISOString(),
    version: '1.0',
    appName: 'Lawn.Smart',
  };
  const blob    = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = `lawnsmart-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MigrationBanner() {
  const [permanentlyDismissed, setPermanentlyDismissed] = useState(() => !!localStorage.getItem(DISMISS_KEY));
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [exported, setExported]   = useState(false);
  const [neverShow, setNeverShow] = useState(false);

  if (window.location.origin !== WWW_ORIGIN || !hasMeaningfulData() || permanentlyDismissed || sessionDismissed) return null;

  const handleExport = () => {
    exportData();
    setExported(true);
  };

  const handleDismiss = () => {
    if (neverShow) {
      localStorage.setItem(DISMISS_KEY, '1');
      setPermanentlyDismissed(true);
    } else {
      setSessionDismissed(true);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        fontFamily: font,
      }}>
        {/* Modal */}
        <div style={{
          background: T.white,
          borderRadius: 18,
          width: '100%',
          maxWidth: 440,
          boxShadow: `0 8px 40px ${T.shadow}`,
          overflow: 'hidden',
        }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1a3d1c, #1e5e20)',
            padding: '20px 22px 18px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <img src="/logo192.png" alt="Lawn.Smart" style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.white, lineHeight: 1.2 }}>
                Lawn<span style={{ color: T.teal }}>.Smart</span>
                <span style={{ fontWeight: 400, fontSize: 13, color: 'rgba(255,255,255,0.6)', marginLeft: 6 }}>v{packageJson.version}</span>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                Important — App moving to a new address
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 22px' }}>

            {/* Deadline notice */}
            <div style={{
              background: T.amberLight,
              border: `1.5px solid ${T.amber}55`,
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 18,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 16, lineHeight: 1.3, flexShrink: 0 }}>📅</span>
              <div style={{ fontSize: 13, color: T.amber, fontWeight: 600, lineHeight: 1.4 }}>
                Effective <strong>{CUTOVER_DATE}</strong>, all traffic will redirect to{' '}
                <strong>app.lawnsmartapp.com</strong>. Any data not migrated beforehand will not be preserved — though you can re-enter it at any time.
              </div>
            </div>

            {/* Steps */}
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>
              How to move your data (2 steps):
            </div>

            {/* Step 1 */}
            <div style={{
              border: `1.5px solid ${exported ? T.green200 : T.border}`,
              background: exported ? T.green50 : T.white,
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 10,
              transition: 'all 0.2s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: exported ? T.green700 : T.amber,
                  color: T.white, fontSize: 12, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.2s',
                }}>
                  {exported ? '✓' : '1'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Export your data</div>
                  <div style={{ fontSize: 11, color: T.textMed, marginTop: 2 }}>
                    Downloads a backup file with your tasks, notes, and settings.
                  </div>
                </div>
                <button
                  onClick={handleExport}
                  style={{
                    background: exported ? T.green700 : T.amber,
                    color: T.white, border: 'none', borderRadius: 8,
                    padding: '7px 14px', fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', flexShrink: 0, fontFamily: font,
                    transition: 'background 0.2s',
                  }}
                >
                  {exported ? 'Downloaded ✓' : 'Export'}
                </button>
              </div>
            </div>

            {/* Step 2 */}
            <div style={{
              border: `1.5px solid ${T.border}`,
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 18,
              opacity: exported ? 1 : 0.55,
              transition: 'opacity 0.2s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: T.green700,
                  color: T.white, fontSize: 12, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  2
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Import at the new address</div>
                  <div style={{ fontSize: 11, color: T.textMed, marginTop: 2 }}>
                    Go to <strong>app.lawnsmartapp.com</strong> → Settings → Data → Import Data.
                  </div>
                </div>
                <a
                  href={exported ? `${APP_URL}` : undefined}
                  onClick={e => { if (!exported) e.preventDefault(); }}
                  style={{
                    background: exported ? T.green700 : T.textLight,
                    color: T.white, borderRadius: 8,
                    padding: '7px 14px', fontSize: 12, fontWeight: 700,
                    textDecoration: 'none', flexShrink: 0,
                    cursor: exported ? 'pointer' : 'default',
                    transition: 'background 0.2s',
                    display: 'inline-block',
                  }}
                >
                  Open App →
                </a>
              </div>
            </div>

            {/* Cloud sync note */}
            <div style={{
              background: T.green50,
              border: `1px solid ${T.green200}`,
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 18,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 15, lineHeight: 1.3, flexShrink: 0 }}>☁️</span>
              <div style={{ fontSize: 12, color: T.textMed, lineHeight: 1.5 }}>
                <strong style={{ color: T.green700 }}>Coming soon:</strong> Cloud sync will automatically back up and sync your data across devices — no manual exports needed.
              </div>
            </div>

            {/* Never show again checkbox */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 10, cursor: 'pointer',
              justifyContent: 'center',
            }}>
              <input
                type="checkbox"
                checked={neverShow}
                onChange={e => setNeverShow(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: T.green700, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: T.textMed, userSelect: 'none' }}>
                Don't show this again
              </span>
            </label>

            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              style={{
                width: '100%', background: 'none',
                border: `1.5px solid ${T.border}`,
                borderRadius: 10, padding: '10px',
                fontSize: 13, fontWeight: 600,
                color: T.textMed, cursor: 'pointer',
                fontFamily: font,
              }}
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
